/* global BaseUrl */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { helperGetRelativeUrl } from 'helpers/RouteHelper';
import { getRequiresAuth } from 'helpers/ContentHelper';
import { format } from 'helpers/DateTimeHelper';

const CONTEXT = 'http://schema.org/';
const TYPE_MOVIE = 'Movie';
const TYPE_TV_CLIP = 'TVClip';
const TYPE_TV_EPISODE = 'TVEpisode';
const TYPE_TV_SERIES = 'TVSeries';
const TYPE_TV_SEASON = 'TVSeason';
const TYPE_PUBLICATION_EVENT = 'PublicationEvent';
const TYPE_COUNTRY = 'COUNTRY';
const TYPE_VIDEO_OBJECT = 'VideoObject';
const TYPE_WATCH_ACTION = 'WatchAction';
const TYPE_WEB_PAGE = 'WebPage';
const TYPE_OFFER = 'Offer';
const DEFAULT_COUNTRY = 'US';
const videoTypeMap = {
  fullEpisode: TYPE_TV_EPISODE,
  clip: TYPE_TV_CLIP,
  movie: TYPE_MOVIE,
};

const eligibleRegions = regions => {
  if (!regions) return null;
  return regions.map(region => ({ '@type': TYPE_COUNTRY, name: region }));
};

const regions = ['US', 'AS', 'GU', 'PR', 'VI', 'UM'];

const createSharedSchema = ({ item, isVideo, referenceDate }) => {
  if (!item) return null;

  const {
    id,
    available,
    dateCreated,
    description,
    duration: itemDuration,
    endDate,
    episodeNumber,
    externalId,
    id: videoId,
    images,
    name,
    originalAirDate,
    seasonNumber,
    videoType,
  } = item;

  const requiresAuth = getRequiresAuth({ video: item });
  const category = !requiresAuth ? 'free' : 'externalSubscription';
  const season = seasonNumber || parseInt(window.location.hash.split('#season-')[1], 10);
  const type = videoTypeMap[videoType];
  const freewheelId = _.get(externalId, '0', null);
  const image = _.get(images, 'still.raw', null);
  const timeRequired = itemDuration ? `P${itemDuration}` : null;
  const trackingQuery = 'cmpid=org=dcg::ag=::mc=seo::src=google::cmp=watchnowaction::add=';
  const availabilityEnds = endDate;
  let potentialAction = null;

  if (isVideo) {
    potentialAction = {
      '@type': TYPE_WATCH_ACTION,
      target: [
        {
          '@type': 'EntryPoint',
          urlTemplate: `http://www.fox.com/watch/${videoId}?${trackingQuery}`,
          actionPlatform: 'http://schema.org/DesktopWebPlatform',
          inLanguage: 'en',
        },
        {
          '@type': 'EntryPoint',
          urlTemplate: `foxapp://video_fullepisode/freewheel/${freewheelId}?${trackingQuery}`,
          actionPlatform: 'http://schema.org/MobileWebPlatform',
          inLanguage: 'en',
        },
        {
          '@type': 'EntryPoint',
          urlTemplate: `android-app://com.fox.now/foxapp/x-callback-url/episode?freewheelid=${freewheelId}&autoplay=true&${trackingQuery}`,
          actionPlatform: 'http://schema.org/AndroidPlatform',
          inLanguage: 'en',
        },
      ],
      expectsAcceptanceOf: {
        '@type': TYPE_OFFER,
        availabilityStarts: available,
        availabilityEnds,
        category,
        eligibleRegion: eligibleRegions(regions),
      },
    };
  }

  const data = {
    '@context': CONTEXT,
    '@id': id,
    '@type': type,
    name,
    description,
    timeRequired,
    image,
    releasedEvent: {
      '@type': TYPE_PUBLICATION_EVENT,
      startDate: referenceDate || originalAirDate || dateCreated,
      location: {
        '@type': TYPE_COUNTRY,
        name: DEFAULT_COUNTRY,
      },
    },
    potentialAction,
  };

  if (season) {
    data.partOfSeason = {
      '@type': TYPE_TV_SEASON,
      seasonNumber: season,
    };
  }

  if (episodeNumber) {
    data.episodeNumber = episodeNumber;
  }

  return data;
};

export const createWatchEpisodeSchema = ({ item }) => {
  if (!item) return undefined;
  const {
    datePublished,
    description,
    images,
    imdbTitleUrl,
    name,
    playerScreenUrl,
    seriesName,
    seriesScreenUrl,
    videoType,
  } = item;

  const type = videoTypeMap[videoType];
  if (type !== TYPE_TV_EPISODE) return undefined;
  const sharedData = createSharedSchema({ item, isVideo: true });
  const baseUrl = BaseUrl;
  const seriesUrl = `${baseUrl}${helperGetRelativeUrl({ url: seriesScreenUrl })}`;
  const url = `${baseUrl}${helperGetRelativeUrl({ url: playerScreenUrl || seriesScreenUrl })}`;

  const partOfSeries = {
    '@type': TYPE_TV_SERIES,
    name: seriesName,
    sameAs: imdbTitleUrl,
    url: seriesUrl,
  };

  const video = {
    '@context': 'https://schema.org',
    '@type': TYPE_VIDEO_OBJECT,
    mainEntityOfPage: { '@type': TYPE_WEB_PAGE, '@id': url },
    name,
    description,
    thumbnailUrl: _.get(images, 'still.raw', null),
    publisher: {
      '@context': 'http://schema.org',
      '@type': 'Organization',
      name: 'FOX',
      url: 'https://www.fox.com/',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.fox.com/assets/images/affiliates/logos/fox.png',
      },
    },
    uploadDate: format(new Date(datePublished), 'YYYY-MM-DD'),
  };

  return {
    ...sharedData,
    url,
    video,
    partOfSeries,
  };
};

export const createWatchClipSchema = ({ item }) => {
  if (!item) return undefined;
  const { name, description, datePublished, duration, images } = item;
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name,
    description,
    thumbnailUrl: _.get(images, 'still.raw', null),
    uploadDate: format(new Date(datePublished), 'YYYY-MM-DD'),
    duration,
  };
};

export const createSeriesDetailSchema = ({ item }) => {
  if (!item) return null;
  const { screenUrl, releaseYear, showCode, actors, director } = item;

  const requiresAuth = getRequiresAuth({ video: item });
  const sharedData = createSharedSchema({ item, referenceDate: releaseYear });
  delete sharedData.timeRequired;
  const sameAs = _.get(item, 'latestEpisode.imdbTitleUrl');
  const baseUrl = BaseUrl;
  const url = `${baseUrl}${helperGetRelativeUrl({ url: screenUrl })}`;
  const trackingQuery = 'cmpid=org=dcg::ag=::mc=seo::src=google::cmp=watchnowaction::add=';
  let actor;

  const potentialAction = {
    '@type': TYPE_WATCH_ACTION,
    target: [
      {
        '@type': 'EntryPoint',
        urlTemplate: `${url}?${trackingQuery}`,
        actionPlatform: 'http://schema.org/DesktopWebPlatform',
        inLanguage: 'en',
      },
      {
        '@type': 'EntryPoint',
        urlTemplate: `ios-app://seriesDetail/${showCode}?${trackingQuery}`,
        application: {
          '@type': 'SoftwareApplication',
          name: 'Fox',
          operatingSystem: 'iOS',
          applicationCategory: 'Multimedia',
        },
        actionPlatform: 'http://schema.org/IOSPlatform',
        inLanguage: 'en',
      },
      {
        '@type': 'EntryPoint',
        urlTemplate: `android-app://seriesDetail/${showCode}?${trackingQuery}`,
        actionPlatform: 'http://schema.org/AndroidPlatform',
        inLanguage: 'en',
      },
    ],
    expectsAcceptanceOf: {
      '@type': TYPE_OFFER,
      availabilityStarts: 2017,
      availabilityEnds: 2030,
      category: !requiresAuth ? 'free' : 'externalSubscription',
      eligibleRegion: eligibleRegions(regions),
    },
  };

  if (!_.isEmpty(actors)) {
    actor = _.map(actors, a => ({
      '@type': 'Person',
      name: a.name,
      url: `${url}credits/${a.alternateId}`,
    }));
  }

  return {
    ...sharedData,
    '@type': TYPE_TV_SERIES,
    '@id': url,
    potentialAction,
    ...(director && { director }),
    ...(sameAs && { sameAs }),
    url,
    ...(actor && { actor }),
  };
};

export const createFoxNowAppFAQSchema = item => {
  if (!item) return null;
  return <script type="application/ld+json">{JSON.stringify(item)}</script>;
};

export const createSchemaTag = ({ item, getSchema }) => {
  if (!item || !getSchema) return null;
  const data = getSchema({ item });
  const dataJSON = JSON.stringify(data);
  return (
    <script key={item.id} type="application/ld+json">
      {dataJSON}
    </script>
  );
};
createSchemaTag.propTypes = {
  getSchema: PropTypes.func.isRequired,
  item: PropTypes.shape({}).isRequired,
};
